import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Check } from "lucide-react";
import { toast } from "sonner";
import { CvShell } from "@/components/cv/CvShell";
import { cvApi } from "@/lib/api";
import { CV_TEMPLATE_CATEGORIES, CV_TEMPLATE_FILTERS } from "@/components/cv/cvContent";

const TEMPLATES = [
  { name: "ATS Prosty", ats: true, cols: 1 },
  { name: "Klasyczny", ats: false, cols: 1 },
  { name: "Nowoczesny", ats: false, cols: 2 },
  { name: "Minimalistyczny", ats: false, cols: 1 },
  { name: "Elegancki ze zdjęciem", ats: false, cols: 2 },
  { name: "Techniczny", ats: false, cols: 2 },
];

export default function CvTemplates() {
  const navigate = useNavigate();
  const [cat, setCat] = useState("Polecane");
  const [filters, setFilters] = useState([]);
  const toggle = (f) => setFilters((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  // Otwiera podgląd ATS dla najnowszego CV użytkownika (lub kieruje do „Moje CV").
  const openAts = async () => {
    try {
      const d = await cvApi.list();
      const items = d.items || [];
      if (items.length) navigate(`/cv/szablon/${items[0].id}`);
      else { toast.info("Najpierw uzupełnij CV w kreatorze."); navigate("/cv/moje"); }
    } catch {
      navigate("/cv/moje");
    }
  };

  return (
    <CvShell active="szablony">
      <div data-testid="cv-templates" className="pb-6">
        <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Szablony CV</h1>
        <p className="mt-2 max-w-2xl text-brand-sec">
          Treść jest niezależna od wyglądu — zmiana szablonu nie usuwa danych. Wersja ATS jest oznaczona osobno.
        </p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="cv-template-categories">
          {CV_TEMPLATE_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`cv-template-cat-${c}`}
              onClick={() => setCat(c)}
              aria-pressed={cat === c}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                cat === c ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {CV_TEMPLATE_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => toggle(f)}
              className={`rounded-full px-3 py-1 text-[12.5px] transition-colors ${
                filters.includes(f) ? "border border-brand-accent/60 text-brand-text" : "border border-brand-line text-brand-muted hover:text-brand-sec"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="cv-template-grid">
          {TEMPLATES.map((t) => (
            <div key={t.name} data-testid="cv-template-card" className="overflow-hidden rounded-panel border border-brand-line bg-brand-surface">
              <div className="relative aspect-[1/1.3] bg-[#f3f1ea] p-4 text-[#111]">
                {t.ats && (
                  <span className="absolute right-3 top-3 rounded-full bg-brand-accent px-2 py-0.5 text-[10px] font-bold text-brand-on">ATS</span>
                )}
                <div className="h-3 w-1/2 rounded bg-black/50" />
                <div className={`mt-3 grid gap-2 ${t.cols === 2 ? "grid-cols-[1fr_2fr]" : "grid-cols-1"}`}>
                  {[...Array(t.cols === 2 ? 8 : 6)].map((_, i) => (
                    <div key={i} className="h-1.5 rounded bg-black/15" />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between p-4">
                <span className="text-sm font-medium text-brand-text">{t.name}</span>
                <div className="flex gap-2">
                  <button onClick={t.ats ? openAts : undefined} data-testid={t.ats ? "cv-template-ats-preview" : undefined} className="inline-flex items-center gap-1 rounded-button border border-brand-line px-3 py-1.5 text-[12.5px] text-brand-sec hover:text-brand-text"><Eye size={14} /> Podgląd</button>
                  <button onClick={t.ats ? openAts : undefined} className="inline-flex items-center gap-1 rounded-button bg-brand-accent px-3 py-1.5 text-[12.5px] font-semibold text-brand-on"><Check size={14} /> Wybieram</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[13px] text-brand-muted">
          To podgląd galerii. Finalne szablony wdrażamy pojedynczo po zatwierdzeniu wzorca referencyjnego i kompletnego szablonu ATS.
        </p>
      </div>
    </CvShell>
  );
}
